/**
 * DashboardWidget — Enterprise Design System
 *
 * A compact dashboard metric card displaying an icon, numeric value,
 * title, optional subtitle, trend indicator, sparkline placeholder,
 * and quick action link.
 *
 * Props:
 *   - icon: MUI icon component (renders inside 42×42 accent container)
 *   - value: string/number metric value (max 12 chars, ellipsis overflow)
 *   - title: string (max 40 chars)
 *   - subtitle: string (max 60 chars, optional)
 *   - trend: { value: number, direction: 'up'|'down' } (optional)
 *   - sparkline: boolean — show 64px sparkline placeholder area
 *   - action: { label: string, onClick: function } — quick action link (max 30 chars)
 *   - accentColor: string (default #1a56db) — icon bg tint + value text
 *   - onClick: function — enables hover elevation + pointer cursor
 *
 * Requirements: 11.1, 11.2, 11.3, 11.4, 11.5, 11.6, 11.7
 */
import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import {
  COLOR_BORDER,
  RADIUS_LG,
} from '../theme/tokens';

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Truncate a string to maxLen, appending ellipsis if trimmed.
 */
function truncate(str, maxLen) {
  if (!str) return '';
  const s = String(str);
  return s.length > maxLen ? s.slice(0, maxLen - 1) + '…' : s;
}

/**
 * Convert a hex color to rgba with given alpha.
 */
function hexToRgba(hex, alpha) {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ── Component ─────────────────────────────────────────────────────────────────

function DashboardWidget({
  icon,
  value,
  title,
  subtitle,
  trend,
  sparkline,
  action,
  accentColor = '#1a56db',
  onClick,
}) {
  const Icon = icon;
  const isClickable = typeof onClick === 'function';

  // Format the display value (max 12 chars)
  const displayValue = value != null ? truncate(String(value), 12) : '—';

  // Trend formatting
  const renderTrend = () => {
    if (!trend || trend.value === 0) return null;
    const isUp = trend.direction === 'up';
    const arrow = isUp ? '↑' : '↓';
    const color = isUp ? '#059669' : '#dc2626';
    const formatted = `${arrow} ${Math.abs(trend.value).toFixed(1)}%`;

    return (
      <Typography
        component="span"
        sx={{
          fontSize: '0.75rem',
          fontWeight: 600,
          color,
          ml: 1,
        }}
      >
        {formatted}
      </Typography>
    );
  };

  return (
    <Box
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      sx={{
        borderRadius: RADIUS_LG,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
        padding: '16px',
        bgcolor: 'background.paper',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'box-shadow 180ms ease, transform 180ms ease',
        ...(isClickable && {
          '&:hover': {
            boxShadow: '0 4px 16px rgba(15,23,42,0.10)',
            transform: 'translateY(-2px)',
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: accentColor,
            outlineOffset: '2px',
          },
        }),
      }}
    >
      {/* Icon Container */}
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: RADIUS_LG,
          backgroundColor: hexToRgba(accentColor, 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.25,
          flexShrink: 0,
        }}
      >
        {Icon ? (
          <Icon sx={{ fontSize: 18, color: accentColor }} />
        ) : null}
      </Box>

      {/* Value + Trend Row */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 0.4, minWidth: 0 }}>
        <Typography
          component="span"
          sx={{
            fontSize: '1.5rem',
            fontWeight: 800,
            color: accentColor,
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
            flex: '0 1 auto',
            '@keyframes fadeInUp': {
              from: { opacity: 0, transform: 'translateY(4px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
            animation: 'fadeInUp 0.3s ease-out',
          }}
        >
          {displayValue}
        </Typography>
        {renderTrend()}
      </Box>

      {/* Title */}
      {title && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            lineHeight: 1.4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
        >
          {truncate(title, 40)}
        </Typography>
      )}

      {/* Subtitle */}
      {subtitle && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.4,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
        >
          {truncate(subtitle, 60)}
        </Typography>
      )}

      {/* Sparkline Placeholder */}
      {sparkline && (
        <Box
          aria-hidden="true"
          sx={{
            height: 64,
            mt: 1.5,
            borderRadius: '6px',
            backgroundColor: '#f8fafc',
            border: `1px dashed ${COLOR_BORDER}`,
          }}
        />
      )}

      {/* Quick Action Link */}
      {action && action.label && (
        <Link
          component="button"
          variant="caption"
          onClick={(e) => {
            e.stopPropagation();
            if (action.onClick) action.onClick();
          }}
          sx={{
            display: 'block',
            mt: 1.5,
            fontWeight: 600,
            color: accentColor,
            textDecoration: 'none',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {truncate(action.label, 30)}
        </Link>
      )}
    </Box>
  );
}

export default DashboardWidget;
